import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import classnames from 'classnames'
import { format } from 'date-fns'

import { Typography, Button, Spinner } from '@telavita-core/react-design-kit'
import HelpIcon from 'components/CoreIcons/Icons/Help'
import ArrowLeftIcon from 'components/CoreIcons/Icons/ArrowLeft'
import ArrowRightIcon from 'components/CoreIcons/Icons/ArrowRight'
import { HelpModal } from 'containers/ModalManager/Modals/HelpModal'

import { ReactComponent as CheckIcon } from 'static/svg/icon-check-green.svg'
import { ReactComponent as DangerIcon } from 'static/svg/icon-check-red.svg'
import { ReactComponent as FilterIcon } from 'static/svg/filter-list.svg'

import { handleQuestions } from './questions'

import { ScheduleContainerFrequencyOptions } from 'containers/ScheduleContainer/_settings'
import { DISAPPROVED, INVALIDATED, FINISHED, WAITING_VALIDATION, VALIDATED, PENDING_AUTHORIZATION } from 'settings/_creditStatusSettings'
import { AnimatePresence } from 'framer-motion'
import {
  ALLOWED_TO_SCHEDULE,
  CONSULTATION_SCHEDULED,
  INELIGIBLE,
  MEDICAL_RETURN_AVAILABLE,
  MEDICAL_RETURN_SCHEDULED,
  SCHEDULE_CYCLE_END
} from 'settings/_patientTreatmentStatus'

import clsx from 'clsx'
import { RequestTreatmentModal } from 'components/RequestTreatmentModal'
import PatientTreatmentsEligibility from 'components/PatientTreatmentsEligibility'
import { PlanGroupCategoryEnum, planGroupsSettings } from 'settings/_planGroupSettings'
import { REQUEST_DISAPPROVAL_REASONS } from 'settings/_requestDisapprovalReasons'
import { useAllowsAutomaticSchedulingThroughReservation } from 'hooks'
import { useGetPatientReservation } from 'views/Dashboard/Sections/PatientHome/ReservationCard/hooks/useGetPatientReservation'
import PopoverMenu from 'components/PopoverMenu'
import { PATIENT_SAVED_PLAN } from 'settings/_storageFlagsSettings'
import { MedicalRequestModal } from 'containers/ModalManager/Modals/MedicalRequestModal'
import { INACTIVE } from 'settings/_personSettings'

export const CategoriesQuestion = {
  SOLICITED_NEW_CREDITS: 'solicitedNewCredits',
  WITH_CREDITS_AND_NOT_EXPIRED: 'withCreditsAndNotExpired',
  TREATMENT_VALIDITY_DISAPPROVED: 'treatmentValidityDisapproved',
  WITHOUT_CREDITS_AND_NOT_EXPIRED: 'withoutCreditsAndNotExpired',
  SOLICITED_NEW_CREDITS_AND_WAITING_MEDICAL_REQUEST:
    'solicitedNewCreditsAndWaitingMedicalRequest',
}

const {
  VITTA
} = PlanGroupCategoryEnum

const PatientEligibility = ({
  patientPlans = [],
  variant,
  isEnterprise,
  isCareplus,
  isCassi,
  isCapitation,
  professionalProfileRole,
  showRequestTreatment,
  isProfessionalProfile = false,
  isCorporate,
  hasButton,
  planGroupName,
  planName,
  planCode,
  creditsStatus,
  availableCredits,
  maxCredits,
  enableDetails,
  validDate,
  expired,
  errorButton,
  loadingButton,
  solicitationDate,
  isUnlimited,
  startDate,
  loading,
  corporateEligibility,
  onClickRequestCreditsButton,
  forceUpdate,
  psychiatryEligibility,
  psychiatryEligibilityLoading,
  onForceUpdate,
  cardNumber,
  onRequestTreatement,
  planGroupCode,
  reasonCode,
  handleUpdateSelectedPlan,
  showFullPlanName = false,
  showPlanSwitch,
  showPsychiatry = false,
  creditsPanelErrorMessage,
  username,
  patientSelectedPlan = null
}) => {
  const [helpModal, setHelpModal] = useState(false)
  const [requestTreatmentModalOpen, setRequestTreatmentModalOpen] = useState(
    false
  )
  const [professionalRole, setProfessionalRole] = useState()
  const [relatedQuestions, setRelatedQuestions] = useState([])
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0)
  const [activeQuestionId, setActiveQuestionId] = useState(null)
  const [checkIcon, setCheckIcon] = useState(false)
  const [dangerIcon, setDangerIcon] = useState(false)

  const currentUserPlans = useSelector(
    (state) => state?.auth?.profileSelected?.plans?.filter(plan => plan.status_code !== INACTIVE)
  )
  const currentPlanInfo = currentUserPlans.find(plan => plan.partner_plan_code === planCode)

  const allowsAutomaticSchedulingThroughReservation = useAllowsAutomaticSchedulingThroughReservation()
  const { reservations } = useGetPatientReservation()
  const hasReservation = Array.isArray(reservations) ? reservations.length > 0 : !!reservations
  const hasReservationWithAutomaticScheduling = allowsAutomaticSchedulingThroughReservation && hasReservation
  const isCreditsPanelVariant = variant === 'creditsPanel'

  /*
    Essa implementação se trata de um experimento que será feito apenas com novos pacientes Care Plus,
    o intuito é mostrar para o paciente no Painel do Convênio e na "Barra Amarela" no perfil do profissional
    a mesma informação que é mostrada para corporates ilimitados hoje, ou seja, "Você possui 1 consulta disponível por semana".

    Esse experimento tem a finalidade de testar uma hipótese, que é aumentar o número de sessões em cerca de 10%.
    Ref: https://app.clickup.com/t/86a1wyu2z
  */
  const BASE_EXPERIMENT_CREDITS_AMOUNT = 99
  const isCreditAmountValid = maxCredits === BASE_EXPERIMENT_CREDITS_AMOUNT
  const isCreditStatusValid = creditsStatus === VALIDATED
  const isPatientAllowedToExperiment = isCareplus && isCreditAmountValid && isCreditStatusValid
  /*
    TODO: Ao final do experimento, remover todas as referências a esse código.
  */

  const Specialities = [
    {
      name: 'Psicologia',
      role: 'psicólogo',
      code: 'PSIC',
      profileCode: 'TVTVPSIC'
    },
    showPsychiatry && {
      name: 'Psiquiatria',
      role: 'psiquiatra',
      code: 'PSIQ',
      profileCode: 'TVTVPSIQ'
    }
  ].filter(speciality => typeof speciality !== 'boolean')

  const planSwitchOptions = patientPlans.reduce((acc, curr) => {
    if (!curr.partner_plan_is_particular && curr.plan_group.category !== VITTA) {
      acc.push({
        id: curr.partner_plan_code,
        title: curr.plan_group.name,
        subTitle: curr.partner_plan_name,
        onClick: () => {
          localStorage.setItem(PATIENT_SAVED_PLAN, curr.partner_plan_code)
          handleUpdateSelectedPlan(curr.partner_plan_code)
          setRelatedQuestions([])
          setDangerIcon(false)
          setCheckIcon(false)
        },
      })
    }

    return acc
  },[])

  const orderedPlanSwitchOptions = planSwitchOptions.sort((a, b) => {
    const aTitle = a.title.toLowerCase()
    const bTitle = b.title.toLowerCase()
    if (aTitle < bTitle) {
      return -1
    }
    if (aTitle > bTitle) {
      return 1
    }
    return 0
  })

  const selectedPlan = patientSelectedPlan || localStorage.getItem(PATIENT_SAVED_PLAN)
  const isEligibleToScheduleInCurrentPlan = patientPlans.find(plan => selectedPlan === (plan.plan_code || plan.partner_plan_code))?.eligible_to_schedule !== false

  const {
    TREATMENT_VALIDITY_DISAPPROVED,
    WITHOUT_CREDITS_AND_NOT_EXPIRED,
    SOLICITED_NEW_CREDITS,
    WITH_CREDITS_AND_NOT_EXPIRED,
    SOLICITED_NEW_CREDITS_AND_WAITING_MEDICAL_REQUEST,
  } = CategoriesQuestion

  const hasRelatedQuestions = !relatedQuestions.length
  const isQuestionIndexEnd = activeQuestionIndex + 1 >= relatedQuestions.length
  const isQuestionIndexStart = activeQuestionIndex === 0

  const isLoadingEligibility = loading || psychiatryEligibilityLoading
  const showSpecialitiesTabs =
    !isLoadingEligibility && !isCreditsPanelVariant && showPsychiatry
  const showPsychiatryCreditsPanel =
    !isLoadingEligibility &&
    professionalProfileRole === 'PSIQ' && isCreditsPanelVariant
  const showPsychologyCreditsPanel =
    !isLoadingEligibility &&
    ((!showPsychiatry && !isCreditsPanelVariant) ||
      professionalProfileRole === 'PSIC')

  useEffect(() => {
    setRelatedQuestions([])
  }, [forceUpdate])

  const infoParagraphStyle = classnames('PatientEligibility__text', {
    'PatientEligibility__text--available': isCorporate
      ? !expired && corporateEligibility.availableCredits > 0
      : !expired && availableCredits > 0,
    'PatientEligibility__text--unavailable': isCorporate
      ? !expired && corporateEligibility.availableCredits === 0
      : !expired && availableCredits === 0,
    'PatientEligibility__text--expired': expired,
  })

  const dateStyle = classnames('PatientEligibility__text', {
    'PatientEligibility__text--expired-date-corporate':
      isCorporate && expired && corporateEligibility.isEligible,
    'PatientEligibility__text--inelegible-corporate': isCorporate && expired,
    'PatientEligibility__text--inelegible-enterprise': isEnterprise && expired,
  })

  const infoStyle = classnames({
    PatientEligibility__info: true,
    'PatientEligibility__info--clickable': true,
    PatientEligibility__corporate: isCorporate,
    'PatientEligibility__corporate--eligible':
      isCorporate && corporateEligibility.isEligible || isPatientAllowedToExperiment,
    'PatientEligibility__corporate--ineligible':
      isCorporate && !corporateEligibility.isEligible,
  })

  const handleOpenQuestion = (questionId) => {
    setActiveQuestionId(questionId)
    setHelpModal(true)
  }

  const handleClickRequestCreditsButton = () => {
    if (currentPlanInfo.uses_new_medical_request_screen) {
      setRequestTreatmentModalOpen(true)
    } else {
      onClickRequestCreditsButton()
    }
  }

  const prevQuestion = () => {
    setActiveQuestionIndex((prevState) => prevState - 1)
  }

  const nextQuestion = () => {
    setActiveQuestionIndex((prevState) => prevState + 1)
  }

  const handleSuccessMedicalRequest = () => {
    onForceUpdate()
    setRequestTreatmentModalOpen(false)
  }

  const getCorporateFrequency = () => {
    if (
      corporateEligibility.frequency ===
      ScheduleContainerFrequencyOptions.WEEKLY
    )
      return 'semana'
    else if (
      corporateEligibility.frequency ===
      ScheduleContainerFrequencyOptions.BIWEEKLY
    )
      return '2 semanas'
    else if (
      corporateEligibility.frequency ===
      ScheduleContainerFrequencyOptions.QUARTERLY
    )
      return '12 semanas'
  }

  const getMessageColor = () => {
    if (!isEligibleToScheduleInCurrentPlan) return 'danger'

    if (isPatientAllowedToExperiment) {
      return 'success'
    }

    if (isCorporate && corporateEligibility.isEligible) {
      if (isUnlimited) return 'success'
      if (corporateEligibility.availableCredits === 0) return 'danger'
      if (expired) return 'danger'
      return 'success'
    }

    if (isEnterprise) {
      const hasExpiredCredits =
        availableCredits > 0 && expired &&  ![WAITING_VALIDATION, PENDING_AUTHORIZATION].includes(creditsStatus)
      const hasDangerCreditStatus = [DISAPPROVED, INVALIDATED].includes(
        creditsStatus
      )
      const hasZeroCredits = availableCredits === 0
      const solicitedNewCredits =
        [WAITING_VALIDATION, PENDING_AUTHORIZATION].includes(creditsStatus) && !!startDate
      const hasZeroCreditsAndNotSolicitedNewCredits =
        hasZeroCredits && !solicitedNewCredits

      const reasonsToReturnDangerMessageColor = [
        errorButton,
        hasExpiredCredits,
        hasDangerCreditStatus,
        hasZeroCreditsAndNotSolicitedNewCredits,
        creditsPanelErrorMessage,
      ]

      if (hasZeroCredits && !expired && hasReservationWithAutomaticScheduling) {
        return undefined
      }

      if (reasonsToReturnDangerMessageColor.some((reason) => reason)) {
        return 'danger'
      }
    } else if (isCorporate && !corporateEligibility.isEligible) return 'danger'
    else if (
      isCorporate &&
      corporateEligibility.availableCredits > 0 &&
      expired
    )
      return 'danger'
  }

  const daysToAuthorizeProcedure =
    planGroupsSettings[planGroupCode]?.daysToAuthorizeProcedure || 3

  const renderInfo = () => {
    if (!isEligibleToScheduleInCurrentPlan) return

    if (isPatientAllowedToExperiment) {
      return (
        <Typography
          variant='content2'
          inheritColor
          key='semanal'
          dangerouslySetInnerHTML={{
            __html: 'Você tem direito a <strong>1 consulta</strong> a cada <strong>semana</strong>',
          }}
        />
      )
    }

    if (isCorporate) {
      if (isUnlimited) {
        return (
          <Typography
            variant='content2'
            inheritColor
            key='semanal'
            dangerouslySetInnerHTML={{
              __html: `Você tem direito a <strong>1 consulta</strong> a cada <strong>${getCorporateFrequency()}</strong>`,
            }}
          />
        )
      } else {
        return (
          <>
            <div className='PatientEligibility__textGroup'>
              <p className='PatientEligibility__text--label'>
                Créditos disponíveis:
              </p>
              <p className={infoParagraphStyle}>
                {corporateEligibility.availableCredits} de{' '}
                {corporateEligibility.maxCredits}
              </p>
            </div>
            <div className='PatientEligibility__textGroup'>
              <p className='PatientEligibility__text--label'>Válido até:</p>
              <p className={dateStyle}>
                {validDate ? String(format(validDate, 'dd/MM/yyyy')) : ''}
              </p>
            </div>
          </>
        )
      }
    }

    if (isEnterprise) {
      if (enableDetails) {
        return (
          <>
            <div className='PatientEligibility__textGroup'>
              <p className='PatientEligibility__text--label'>
                Créditos disponíveis:
              </p>
              <p className={infoParagraphStyle}>
                {availableCredits} de {maxCredits}
              </p>
            </div>
            <div className='PatientEligibility__textGroup'>
              <p className='PatientEligibility__text--label'>Válido até:</p>
              <p className={dateStyle}>
                {String(format(validDate, 'dd/MM/yyyy'))}
              </p>
            </div>
          </>
        )
      }
    }
  }

  const renderMessage = () => {
    const INELIGIBLE_CORPORATE_FEEDBACK = `
      Seu CPF não consta mais na lista de beneficiários do seu convênio. Infelizmente, isso impossibilita o agendamento de consultas.
      <strong>Se houver algum engano</strong>, entre em contato com nossa equipe pelo chat para mais informações.
      <br /><br />
      Se até a data da próxima consulta seu nome ainda não constar na lista de beneficiários do convênio, todos os próximos agendamentos serão cancelados.
    `

    if (!isEligibleToScheduleInCurrentPlan) {
      return '<strong>Seu nome não consta mais na lista de beneficiários do convênio.</strong> Infelizmente, isso impossibilita agendar e realizar consultas. Se houver algum engano, entre em contato com nossa equipe pelo chat no canto da tela para mais informações.'
    }

    if (isPatientAllowedToExperiment) {
      if(hasReservationWithAutomaticScheduling) {
        return 'Agendamos automaticamente em todos os meses disponíveis no calendário do psicólogo.'
      }
      return 'Agendamentos liberados em todos os meses disponíveis no calendário do psicólogo.'
    }

    if (isCorporate) {
      if (isUnlimited) {
        if (corporateEligibility.isEligible) {
          if(hasReservationWithAutomaticScheduling) {
            return 'Agendamos automaticamente em todos os meses disponíveis no calendário do psicólogo.'
          }
          return 'Agendamentos liberados em todos os meses disponíveis no calendário do psicólogo.'
        } else {
          return INELIGIBLE_CORPORATE_FEEDBACK
        }
      } else {
        if (corporateEligibility.isEligible) {
          if (corporateEligibility.availableCredits === 0) {
            return '*Não há mais consultas disponíveis pelo seu convênio.'
          } else if (corporateEligibility.availableCredits > 0 && expired) {
            return '*A validade do(s) seu(s) crédito(s) de consulta expirou.'
          } else {
            return `Você tem direito a <strong>1 consulta</strong> a cada <strong>${getCorporateFrequency()}</strong>`
          }
        } else {
          return INELIGIBLE_CORPORATE_FEEDBACK
        }
      }
    }

    if (isEnterprise) {
      const withCreditsAndExpired = creditsStatus === FINISHED && expired
      const withCreditsAndNotExpired =
        availableCredits > 0 &&
        !expired &&
        ![DISAPPROVED, WAITING_VALIDATION, PENDING_AUTHORIZATION, INVALIDATED].includes(creditsStatus)
      const solicitedNewCredits =
        [WAITING_VALIDATION, PENDING_AUTHORIZATION].includes(creditsStatus) && startDate
      const solicitedNewCreditsAndWaitingMedicalRequest =
         [WAITING_VALIDATION, PENDING_AUTHORIZATION].includes(creditsStatus) && !startDate
      const treatmentValidityDisapproved = creditsStatus === DISAPPROVED
      const creditsInvalidated = creditsStatus === INVALIDATED
      const withoutCreditsAndNotExpired = availableCredits === 0 && !expired
      const hasDisapprovalReason =
        reasonCode && REQUEST_DISAPPROVAL_REASONS[reasonCode]

      if(creditsPanelErrorMessage) {
        return creditsPanelErrorMessage
      }

      if(withCreditsAndNotExpired) {
        if(hasRelatedQuestions) setRelatedQuestions(handleQuestions(daysToAuthorizeProcedure).filter(question => question.category === WITH_CREDITS_AND_NOT_EXPIRED))
        if(checkIcon) setCheckIcon(false)
        if(dangerIcon) setDangerIcon(false)
        return '<strong>Importante:</strong> a agenda dos profissionais só mostrará disponibilidades até a data de validade dos seus créditos.'
      }

      if (withCreditsAndExpired) {
        if (checkIcon) setCheckIcon(false)
        if (dangerIcon) setDangerIcon(false)
        return '*A validade do(s) seu(s) crédito(s) expirou. Para renovar, use o botão de solicitação abaixo.'
      }

      if (solicitedNewCredits) {
        if (hasRelatedQuestions)
          setRelatedQuestions(
            handleQuestions(daysToAuthorizeProcedure).filter(
              (question) => question.category === SOLICITED_NEW_CREDITS
            )
          )
        if (!checkIcon) setCheckIcon(true)
        if (dangerIcon) setDangerIcon(false)
        return `Solicitação de novos créditos feita em <strong>${solicitationDate}</strong>. <br />Agora é só aguardar a aprovação do seu convênio. <br /><strong>Em até ${daysToAuthorizeProcedure} dias úteis</strong> a partir da data da solicitação, avisaremos por e-mail e SMS.`
      }

      if (solicitedNewCreditsAndWaitingMedicalRequest) {
        if (checkIcon) setCheckIcon(false)
        if (dangerIcon) setDangerIcon(false)
        if (hasRelatedQuestions)
          setRelatedQuestions(
            handleQuestions(daysToAuthorizeProcedure).filter(
              (question) =>
                question.category ===
                SOLICITED_NEW_CREDITS_AND_WAITING_MEDICAL_REQUEST
            )
          )

        return `Sua consulta está em aprovação. Enquanto aguardamos a liberação, <strong>que leva até ${daysToAuthorizeProcedure} dias úteis</strong>, não é possível agendar novas consultas. Mas não se preocupe, avisaremos por e-mail e SMS.`
      }

      if (treatmentValidityDisapproved) {
        if (!dangerIcon) setDangerIcon(true)
        if (checkIcon) setCheckIcon(false)

        if (hasDisapprovalReason)
          return REQUEST_DISAPPROVAL_REASONS[reasonCode]

        if (hasRelatedQuestions)
          setRelatedQuestions(
            handleQuestions(daysToAuthorizeProcedure).filter(
              (question) => question.category === TREATMENT_VALIDITY_DISAPPROVED
            )
          )
        return 'Infelizmente sua solicitação foi reprovada pelo seu convênio. Para mais informações, entre em contato com a nossa equipe de atendimento pelo chat.'
      }

      if (creditsInvalidated) {
        if (checkIcon) setCheckIcon(false)
        if (dangerIcon) setDangerIcon(false)
        return 'Seus créditos estão inválidos e por isso não será possível realizar uma consulta. Caso não reconheça o motivo da invalidação, entre em contato com a nossa equipe de atendimento pelo chat.'
      }

      if (withoutCreditsAndNotExpired) {
        if (hasReservationWithAutomaticScheduling) {
          return '<strong>Importante:</strong> todos os seus créditos foram utilizados para agendar suas consultas automaticamente. Assim que novos créditos forem aprovados, mais consultas serão agendadas automaticamente no dia e horário de sua reserva.'
        }
        if (checkIcon) setCheckIcon(false)
        if (dangerIcon) setDangerIcon(false)
        if (hasRelatedQuestions)
          setRelatedQuestions(
            handleQuestions(daysToAuthorizeProcedure).filter(
              (question) =>
                question.category === WITHOUT_CREDITS_AND_NOT_EXPIRED
            )
          )
        return `*Assim que você realizar todas as consultas agendadas, pediremos a renovação dos seus créditos junto ao convênio e <strong>em até ${daysToAuthorizeProcedure} dias úteis</strong> te avisaremos por e-mail e SMS.`
      }
    }
  }

  const renderContent = () => {
    if (showRequestTreatment && !solicitationDate) {
      return renderRequestTreatmentContent('PSIC')
    }

    return (
      <>
        <div className={infoStyle}>{renderInfo()}</div>
        <div className='PatientEligibility__message'>
          {(dangerIcon || checkIcon) && (
            <div style={{ width: 24, height: 24, marginRight: 10 }}>
              {dangerIcon && <DangerIcon />}
              {checkIcon && <CheckIcon />}
            </div>
          )}
          <Typography
            customClassName='PatientEligibility__messageText'
            variant='content3'
            color={getMessageColor()}
            dangerouslySetInnerHTML={{ __html: renderMessage() }}
          />
        </div>
      </>
    )
  }

  const renderRequestTreatmentContent = (professionalRole) => {
    const currentSpecialty = Specialities.find(
      (specialty) => specialty.code === professionalRole
    )

    return (
      <div className='PatientEligibility__startTreatmentMessage'>
        <Typography
          data-cy='request-treatment-start-message'
          variant='content2'
        >
          Para iniciar um tratamento com um <b>{currentSpecialty?.role}</b>,
          solicite no botão abaixo.
        </Typography>
        <Button
          data-cy='request-treatment-button'
          variant={isCreditsPanelVariant ? 'contained' : 'outlined'}
          uppercase={false}
          customClassName={clsx({
            PatientEligibility__RequestTreatmentButton: true,
            'PatientEligibility__RequestTreatmentButton--creditsPanelVariant': isCreditsPanelVariant,
          })}
          onClick={() => {
            setRequestTreatmentModalOpen(true)
            setProfessionalRole(professionalRole)
          }}
        >
          Solicitar consulta com {currentSpecialty?.role}
        </Button>
      </div>
    )
  }

  const renderPsiqContent = () => {
    const { cycleStatus, cycleDate } = psychiatryEligibility

    const formatedCycleDate =
      cycleDate && format(new Date(cycleDate), 'dd/MM/yyyy')

    if (cycleStatus === ALLOWED_TO_SCHEDULE) {
      return (
        <Typography
          variant='content2'
          inlineStyles={isCreditsPanelVariant ? { textAlign: 'center' } : {}}
        >
          O agendamento com psiquiatra está liberado.
          {isCreditsPanelVariant && <br />} Você pode agendar{' '}
          <b className='PatientEligibility__Highlight__Green'>
            1 consulta por vez
          </b>
          .
        </Typography>
      )
    }

    if (cycleStatus === CONSULTATION_SCHEDULED) {
      return (
        <>
          <Typography variant='content2'>
            Você já possui uma consulta agendada com psiquiatra.
          </Typography>
          <Typography
            variant='content3'
            color='danger'
          >
            Só é possível agendar uma nova depois que você realizá-la.
          </Typography>
          </>
      )
    }

    if (cycleStatus === MEDICAL_RETURN_AVAILABLE) {
      return (
        <>
          <Typography variant='content2'>
            Você tem direito a um{' '}
            <b className='PatientEligibility__Highlight__Green'>
              retorno médico
            </b>{' '}
            com o seu psiquiatra.
          </Typography>
          <Typography color='success' variant='content3'>
            Agende com o mesmo profissional até {formatedCycleDate}.
          </Typography>
        </>
      )
    }

    if (cycleStatus === MEDICAL_RETURN_SCHEDULED) {
      return (
        <>
          <Typography variant='content2'>
            Você já possui um retorno médico agendado com psiquiatra.
          </Typography>
          <Typography variant='content3' color='danger'>
            Só é possível agendar uma nova consulta a partir de{' '}
            {formatedCycleDate}.
          </Typography>
        </>
      )
    }

    if (cycleStatus === SCHEDULE_CYCLE_END) {
      return (
        <Typography variant='content2'>
          Você pode agendar uma nova consulta com psiquiatra{' '}
          <b className='PatientEligibility__Highlight__Green'>
            a partir de {formatedCycleDate}.
          </b>
        </Typography>
      )
    }

    if (cycleStatus === INELIGIBLE) {
      return (
        <div className='PatientEligibility__PatientInelegibleContainer'>
          <Typography
            variant='content3'
            color='danger'
            style={{ lineHeight: '16px' }}
          >
            Você não consta mais como beneficiário do seu convênio para esta
            especialidade. Infelizmente, isso impossibilita o agendamento de
            consultas de psiquiatria. Se houver algum engano, entre em contato
            com nossa equipe pelo chat para mais informações.
          </Typography>
        </div>
      )
    }
  }

  return (
    <>
      <div className='PatientEligibility'>
        <div className='PatientEligibility__title'>
          <div className='PatientEligibility__title__wrapper'>
            {showFullPlanName ? planGroupName : 'Meu convênio'}
            <span className='PatientEligibility__title__groupName'>
              {showFullPlanName ? planName : planGroupName}
            </span>
          </div>
          {showPlanSwitch && (
            <PopoverMenu
              label='VISUALIZAR O CONVÊNIO:'
              buttonText='TROCAR'
              headerLabel='VISUALIZAR O CONVÊNIO:'
              customIcon={<FilterIcon />}
              options={orderedPlanSwitchOptions}
              selectedOption={selectedPlan}
            />
          )}
        </div>
        {isLoadingEligibility && <Spinner />}
        {showSpecialitiesTabs && (
          <div className='PatientEligibility__PatientTreatmentsWrapper'>
            <PatientTreatmentsEligibility
              tabs={Specialities.map((specialty) => specialty.name)}
              tabsPanel={[
                <div className='PatientEligibility__tabPanel'>
                  {renderContent()}
                  {
                    hasButton
                      ? loadingButton
                        ? <Spinner customClassName="PatientEligibility__spinner" />
                        : <div className="PatientEligibility__info__credButton">
                          <Button
                            variant={variant === 'creditsPanel' ? 'contained' : 'outlined'}
                            disabled={false}
                            onClick={handleClickRequestCreditsButton}
                            uppercase={variant === 'creditsPanel'}
                            inlineStyles={{
                              height: variant === 'creditsPanel' ? '48px' : '36px',
                              padding: '0 10px',
                              width: variant === 'creditsPanel' && '100%',
                              marginTop: variant === 'creditsPanel' && '20px',
                            }}
                          >
                            Solicitar novos créditos
                          </Button>
                        </div>
                      : null
                  }
                  {
                    relatedQuestions.length
                      ? (
                        <div className='PatientEligibility__footer'>
                          <div className='PatientEligibility__footer__wrapper'>
                            <Typography
                              variant='content3'
                              inlineStyles={{
                                color: '#898989',
                                marginBottom: '5px',
                              }}
                            >
                    DÚVIDA FREQUENTE
                            </Typography>

                            <Typography
                              customClassName='PatientEligibility__footer__wrapper__question'
                              variant='content3'
                              color='primary'
                              weight='bold'
                              onClick={() => handleOpenQuestion(relatedQuestions[activeQuestionIndex]?.id)}
                              inlineStyles={{
                                lineHeight: '16px',
                              }}
                            >
                              {
                      relatedQuestions[activeQuestionIndex]?.reducedTitle ||
                      relatedQuestions[activeQuestionIndex]?.title
                              }
                            </Typography>

                          </div>
                          {relatedQuestions.length > 1 && (
                            <div className='PatientEligibility__footer__arrows'>
                              <button
                                onClick={prevQuestion}
                                disabled={isQuestionIndexStart}
                              >
                                <ArrowLeftIcon
                                  fill='#000'
                                  height='24px'
                                  width='24px'
                                />
                              </button>
                              <button
                                onClick={nextQuestion}
                                disabled={isQuestionIndexEnd}
                              >
                                <ArrowRightIcon
                                  fill='#000'
                                  height='24px'
                                  width='24px'
                                />
                              </button>
                            </div>
                          )}
                          {isEnterprise && (
                            <div
                              data-cy='professional-profile-help-button'
                              className='PatientEligibility__title__icon'
                              onClick={() => {
                                setActiveQuestionId(null)
                                setHelpModal(true)
                              }}
                            >
                              <HelpIcon
                                fill='#1F35B5'
                                height='24'
                                width='24'
                                viewBox='0 0 24 24'
                              />
                            </div>
                          )}
                        </div>
                      )
                      : null
                  }
                </div>,
                <div className='PatientEligibility__psiqTabPanel'>
                  {renderPsiqContent()}
                </div>,
              ]}
            />
          </div>
        )}
        {showPsychiatryCreditsPanel && <div className='PatientEligibility__CreditsPanelPsiq'>{renderPsiqContent()}</div>}
        {showPsychologyCreditsPanel ? (
          <div className='PatientEligibility__tabPanel' style={{ alignItems: isCreditsPanelVariant ? 'center' : 'normal' }}>
            <div>
              {renderContent()}
              {hasButton ? (
                loadingButton ? (
                  <Spinner customClassName='PatientEligibility__spinner' />
                ) : (
                  <div className='PatientEligibility__info__credButton'>
                    <Button
                      variant={
                        variant === 'creditsPanel' ? 'contained' : 'outlined'
                      }
                      disabled={false}
                      onClick={handleClickRequestCreditsButton}
                      uppercase={variant === 'creditsPanel'}
                      inlineStyles={{
                        height: variant === 'creditsPanel' ? '48px' : '36px',
                        padding: '0 10px',
                        width: variant === 'creditsPanel' && '100%',
                        marginTop: variant === 'creditsPanel' && '20px',
                      }}
                    >
                      Solicitar novos créditos
                    </Button>
                  </div>
                )
              ) : null}
            </div>
            {relatedQuestions.length ? (
              <div className='PatientEligibility__footer'>
                <div className='PatientEligibility__footer__wrapper'>
                  <Typography
                    variant='content3'
                    inlineStyles={{
                      color: '#898989',
                      marginBottom: '5px',
                    }}
                  >
                    DÚVIDA FREQUENTE
                  </Typography>

                  <Typography
                    customClassName='PatientEligibility__footer__wrapper__question'
                    variant='content3'
                    color='primary'
                    weight='bold'
                    onClick={() =>
                      handleOpenQuestion(
                        relatedQuestions[activeQuestionIndex]?.id
                      )
                    }
                    inlineStyles={{
                      lineHeight: '16px',
                    }}
                  >
                    {relatedQuestions[activeQuestionIndex]?.reducedTitle ||
                      relatedQuestions[activeQuestionIndex]?.title}
                  </Typography>
                </div>
                {relatedQuestions.length > 1 && (
                  <div className='PatientEligibility__footer__arrows'>
                    <button
                      onClick={prevQuestion}
                      disabled={isQuestionIndexStart}
                    >
                      <ArrowLeftIcon fill='#000' height='24px' width='24px' />
                    </button>
                    <button
                      onClick={nextQuestion}
                      disabled={isQuestionIndexEnd}
                    >
                      <ArrowRightIcon fill='#000' height='24px' width='24px' />
                    </button>
                    {isEnterprise && (
                      <div
                        data-cy='professional-profile-help-button'
                        className='PatientEligibility__title__icon'
                        onClick={() => {
                          setActiveQuestionId(null)
                          setHelpModal(true)
                        }}
                      >
                        <HelpIcon
                          fill='#1F35B5'
                          height='24'
                          width='24'
                          viewBox='0 0 24 24'
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        ) : null }
      </div>

      {helpModal && (
        <AnimatePresence>
          <HelpModal
            title={
              showPsychiatry
                ? 'Dúvidas sobre o seu convênio em…'
                : 'Tire suas dúvidas sobre os créditos de convênio'
            }
            questions={handleQuestions(daysToAuthorizeProcedure)}
            onClose={() => setHelpModal(false)}
            activeQuestion={activeQuestionId}
            showTabs={showPsychiatry}
            tabs={Specialities.map((specialty) => ({
              title: specialty.name,
              code: specialty.code,
            }))}
            defaultTab={professionalProfileRole}
          />
        </AnimatePresence>
      )}
      {requestTreatmentModalOpen && (
        currentPlanInfo.uses_new_medical_request_screen ? (
          <MedicalRequestModal onSuccess={handleSuccessMedicalRequest} planCode={planCode} onClose={() => setRequestTreatmentModalOpen(false)} />
        ) : (
          <RequestTreatmentModal
            onClose={() => setRequestTreatmentModalOpen(false)}
            professionalRole={professionalRole}
            variant={isProfessionalProfile ? 'PROFILE' : 'DASHBOARD'}
            onSuccess={onForceUpdate}
            onRequestTreatement={onRequestTreatement}
            planCode={planCode}
            cardNumber={cardNumber}
            isCareplus={isCareplus}
            isCassi={isCassi}
            isCapitation={isCapitation}
            daysToAuthorizeProcedure={daysToAuthorizeProcedure}
            planGroupCode={planGroupCode}
            userName={username}
          />
        )
      )}
    </>
  )
}

export { PatientEligibility }
